import type { LatLng } from './delivery-zones'

/**
 * Where a map opens - FRONTEND.md §9.692 and §9.694, the owner's #13.
 *
 * Two maps open by one rule. The zone editor centres on its zones and, with none drawn, on the depot;
 * the order builder's pin map centres on the delivery and, with no point yet, on the depot. **With
 * neither, the whole of Slovakia - never one town.**
 *
 * **Žilina was the fallback** (`zone-draft.ts`, ported from legacy `delivery-zones.tsx:184-189`,
 * whose comment said *Slovakia default* and whose code said Žilina). A franchise with no zones - Sereď
 * on the day it opened - got a street map of a town it does not deliver to, at street zoom, about
 * 150 km away. The depot is the franchise's own answer to *where is here*, and `GET
 * /delivery-zones/depot` has published it since §10A.121.
 */
export interface MapView {
  centre: LatLng
  zoom: number
}

/**
 * The whole country at a glance: the middle of Slovakia's bounding box, at a zoom where both ends fit a
 * desktop map. A view that claims nothing about where the franchise is.
 */
export const SLOVAKIA_MAP_VIEW: MapView = { centre: { lat: 48.67, lng: 19.7 }, zoom: 7 }

/** A depot is where zones are drawn around: the town and the villages it delivers to. */
export const DEPOT_MAP_ZOOM = 12

/** A delivery is one door, so the pin map opens close enough to see which. */
export const POINT_MAP_ZOOM = 16

/**
 * Two finite numbers, or null - from whatever a payload or a map handed over.
 *
 * **`{ lat: null, lng: null }` is the depot endpoint's own answer** when the address cannot be geocoded,
 * so that is the commonest thing this refuses, not an edge case. A `NaN` is refused too: Leaflet throws on
 * one, and a map that throws is a blank card.
 */
export function usablePoint(value: unknown): LatLng | null {
  if (typeof value !== 'object' || value === null) return null
  const { lat, lng } = value as { lat?: unknown; lng?: unknown }
  if (typeof lat !== 'number' || typeof lng !== 'number') return null
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return { lat, lng }
}

/** The point at a door's zoom, else the depot at a town's, else Slovakia. */
export function mapViewFor(point: unknown, depot: unknown): MapView {
  const here = usablePoint(point)
  if (here !== null) return { centre: here, zoom: POINT_MAP_ZOOM }
  const start = usablePoint(depot)
  if (start !== null) return { centre: start, zoom: DEPOT_MAP_ZOOM }
  return SLOVAKIA_MAP_VIEW
}
